import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';

import useIdleMonitor from './idle-monitor-service';
import { get as _get } from 'lodash';

// const LSCHECK = process.env.BRICATA_APPLIANCE_TYPE === 'ls';

const FetchAPI = ({ method, url, body, notagain, cache, signal, plain, responseType }) => {
    return fetch(`/api/${url}`, {
        method: method || 'GET',
        body: (method || 'GET').toUpperCase() !== 'GET' ? plain ? body : JSON.stringify(body) : undefined,
        cache: cache ? 'force-cache' : 'default',
        headers: AuthorizationHeader(),
        responseType,
        signal
    })
        .then(responseType === 'arraybuffer' ? ArrayBufferHandler : ResponseHandler)
        .catch(error => {
            // This is dirty but changing a password bails as a 401 :(
            // if we run into a second call that can 401 - let's add an optional dont-bail param
            if ([401, 403].indexOf(error.status) !== -1 && !error.url.match('set-password')) {
                // escape hatch
                if (notagain) return LSRestedService.boot();

                // if the refresh token still has some life repeat the failed API call
                // otherwise boot the user out
                return LSRestedService.refresh()
                    .then(() => FetchAPI({ method, url, body, notagain: true }))
                    .catch(() => LSRestedService.boot());
            }
            return Promise.reject(FormatError(error));
        });
};

class LSRestedDataService {
    constructor () {
        // we only need one of these
        if (!LSRestedDataService.instance) {
            LSRestedDataService.instance = this;
        }
        this.searchPlaceholder = 'Search system';

        // stash user and tokens
        this.setSession = (tokens, user) => {
            localStorage.setItem('auth', JSON.stringify({
                token: tokens.access_token,
                refresh_token: tokens.refresh_token,
                user: user,
                timestamp: new Date().getTime(),
            }));
            return true;
        };

        this.login = (username, password) => {
            return fetch('/api/login/', {
                method: 'POST',
                body: JSON.stringify({ username: username, password: password, refresh_token: true })
            })
                .then(ResponseHandler)
                .then(tokens => {
                    return fetch('/api/users/-self', {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${tokens.access_token}` }
                    })
                        .then(ResponseHandler)
                        .then(res => {
                            return {
                                user: res.objects[username],
                                tokens: tokens
                            }
                        });
                })
                .then(res => {
                    this.setSession(res.tokens, res.user);
                    return Promise.resolve(res);
                });
        };

        this.getAuth = () => {
            try {
                return JSON.parse(localStorage.getItem('auth'));
            } catch (err) {
                return false;
            }
        };

        this.logout = () => {
            const auth = this.getAuth();
            // TODO: this api shouldn't need username
            const username = (auth && auth.user) ? auth.user.username : '';
            fetch('api/logout/', {
                method: 'POST',
                body: `{"username": "${username}"}`,
                headers: AuthorizationHeader(),
            });
            localStorage.removeItem('auth');
            this.license = undefined;
        };

        this.boot = () => {
            this.logout();
            location.assign('/signin');
        };

        // this will fail if the refresh token is not good anymore
        this.refresh = async () => {
            const auth = this.getAuth();
            if (!auth) return Promise.reject(new Error('no token'));

            // guard against concurrent calls to refresh token
            const unlock = await this.refreshMux.lock();
            const nAuth = this.getAuth();
            if (nAuth && auth.token !== nAuth.token) {
                unlock();
                return Promise.resolve();
            }
            return FetchAPI({
                method: 'POST',
                url: 'refresh/',
                body: { access_token: auth.token, refresh_token: auth.refresh_token },
                notagain: true
            }).then(res => this.setSession(res, auth.user))
                .finally(() => unlock());
        };

        this.getPasswordToken = (username) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}/password-token`,
            });
        };
        
        // "(GET:/license/validate)"}, license::validate_handler_get, {"admin", "license"}},
        this.ValidateLicense = () => {
            return FetchAPI({
                method: 'GET',
                url: 'license/validate',
            });
        };
        
        // "(GET:/license/(.+))"}, license::handler_get, {"admin", "license"}},
        this.getLicenses = (limit, offset) => {
            return FetchAPI({
                method: 'GET',
                url: `license/?limit=${limit || 25}&offset=${offset || 0}&q=&sort=name:asc`,
            });
        };
        
        // "(PUT:/license/generate/)"}, license::generate_handler_put, {"admin", "license"}},
        this.generateLicense = (data) => {
            return FetchAPI({
                method: 'PUT',
                url: 'license/generate/',
                body: data
            });
        };

        // users
        // "(POST:/users/)"}, users::handler_post, {"admin"}},
        this.createUser = (data) => {
            return FetchAPI({
                method: 'POST',
                url: 'users/',
                body: data
            });
        };
        
        // "(GET:/users/(.+))"}, users::user_handler_get, {"admin", "license"}},
        this.getUsers = () => {
            return FetchAPI({
                method: 'GET',
                url: 'users/?limit=25&offset=0&q=&sort=name:asc',
            });
        };
        
        // "(PUT:/users/(.+)/reset-password)"}, users::user_reset_pass_handler_put, {"notok"}},
        this.resetPassword = (username) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}/reset-password`,
            });
        };
        
        // "(PUT:/users/(.+)/set-password)"}, users::user_finish_set_pass_handler_put, {"notok"}},
        this.setPassword = (username, data) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}/set-password`,
                body: data,
            });
        };

        // "(PUT:/users/(.+)/password-token)"}, users::user_create_set_pass_tok_handler_put, {"admin", "license"}},
        this.setPasswordToken = (username, data) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}/password-token`,
                body: data,
            });
        };
        
        this.getUser = (username) => {
            return FetchAPI({
                method: 'GET',
                url: `users/${username}`,
            });
        };

        // "(GET:/users/(.+)/-self)"}, users::user_self_handler_get, {"notok"}},
        this.getSelf = () => {
            return FetchAPI({
                method: 'GET',
                url: 'users/-self/'
            }).then(res => res.object);
        };

        // "(PUT:/users/(.+))"}, users::handler_put, {"admin"}},
        this.updateUser = (username, data) => {
            return FetchAPI({
                method: 'PUT',
                url: `users/${username}`,
                body: data
            });
        };

        // "(DELETE:/users/(.+))"}, users::handler_delete, {"admin"}},
        this.deleteUser = (username) => {
            return FetchAPI({
                method: 'DELETE',
                url: `users/${username}`,
            });
        };

        // /// ---------------------------- roles
        // "(GET:/roles/(.+))"}, roles::handler_get, {"admin"}},
        this.getRoles = () => {
            return FetchAPI({
                method: 'GET',
                url: `roles/`,
            });
        };
    
        // /// ---------------------------- audit
        // "(GET:/audittrails/(.+))"}, audit::handler_get, {"admin"}},
        this.getAuditTrails = () => {
            return FetchAPI({
                method: 'get',
                url: 'audittrails/',
            });
        };

        return LSRestedDataService.instance;
    }
}

// Add the auth header with a jwt token
function AuthorizationHeader () {
    const auth = LSRestedService.getAuth();
    return auth ? { Authorization: `Bearer ${auth.token}` } : {};
}

// process json or text response
function ResponseHandler (response) {
    if (!response) return '';
    return response.text().then(text => {
        let data = text;
        try {
            data = text && JSON.parse(text);
        } catch (err) {
            // response isn't json
            if ((text || '').trim() !== 'null') {
                data = text;
            }
        }
        if (!response.ok) {
            // just in case text is empty
            console.debug('response handler text', text);
            console.debug('response handler data', data);
            console.debug('response handler response', response);

            const error = text || (data ? data.message : response);
            return Promise.reject(error);
        }
        return data;
    });
}

const FormatError = (err) => {
    let error = err;
    try {
        error = err && JSON.parse(err);
    } catch (e) {
        return err;
    }

    return error;
};

const AuthenticatedRoute = (props) => {
    const auth = LSRestedService.getAuth();
    const timeout = _get(auth, 'user.session_timeout', 20);

    useIdleMonitor({
        timeout: timeout * 60000,
        onTimeout: LSRestedService.boot,
    });

    return auth
        ? <Outlet />
        : <Navigate to='/signin' />;
};

const LSRestedService = new LSRestedDataService();

const reportsFields = [
    {
        variable: 'license',
        hidden: false,
        headerName: 'License',
        field: 'license',
        defaultSort: 'asc',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'valid_until',
        hidden: false,
        headerName: 'Valid Until',
        field: 'valid_until',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'sn',
        hidden: false,
        headerName: 'Serial Number',
        field: 'sn',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'mac',
        hidden: false,
        headerName: 'MAC',
        field: 'mac',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'created',
        hidden: false,
        headerName: 'Created',
        field: 'created',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'organization',
        hidden: false,
        headerName: 'Organization',
        field: 'organization',
        type: 'string',
        sortable: true,
    }
];

const userFields = [
    {
        variable: 'username',
        hidden: false,
        headerName: 'Username',
        field: 'username',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'name',
        hidden: false,
        headerName: 'Display Name',
        field: 'name',
        defaultSort: 'asc',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'email',
        hidden: false,
        headerName: 'Email',
        field: 'email',
        type: 'string',
        sortable: true,
    },
    {
        variable: 'admin',
        hidden: false,
        headerName: 'Admin',
        field: 'permissions',
        type: 'string',
        sortable: true,
    },
];

export {
    LSRestedService as default,
    AuthenticatedRoute,
    reportsFields,
    userFields,
};
